import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiUpload, FiX, FiSave, FiArrowLeft } from 'react-icons/fi';
import { extensionsAPI } from '../services/api';
import './CreateExtension.css'; // Reuse the same CSS

const UpdateExtension = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    version: '1.0.0',
    icon: null,
    file: null,
    isWhitelabeled: false,
    isUpgradeable: true,
    repoMention: '',
    chromeWebStoreLink: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingExtension, setLoadingExtension] = useState(true);

  const categories = [
    'Social & Communication',
    'Productivity',
    'Entertainment',
    'Shopping',
    'News & Weather',
    'Developer Tools',
    'Other'
  ];

  // Fetch extension data on component mount
  useEffect(() => {
    const fetchExtension = async () => {
      try {
        setLoadingExtension(true);
        const response = await extensionsAPI.getById(id);
        
        if (response.success && response.extension) {
          const extension = response.extension;
          setFormData({
            name: extension.name || '',
            description: extension.description || '',
            category: extension.category || '',
            version: extension.version || '1.0.0',
            icon: null, // We'll handle icon separately
            file: null, // We'll handle file separately
            isWhitelabeled: extension.isWhitelabeled || false,
            isUpgradeable: extension.isUpgradeable !== undefined ? extension.isUpgradeable : true,
            repoMention: extension.repoMention || '',
            chromeWebStoreLink: extension.chromeWebStoreLink || ''
          });
        } else {
          toast.error('Failed to load extension data');
          navigate('/dashboard');
        }
      } catch (error) {
        console.error('Error fetching extension:', error);
        toast.error('Failed to load extension data');
        navigate('/dashboard');
      } finally {
        setLoadingExtension(false);
      }
    };

    if (id) {
      fetchExtension();
    }
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'icon' && !file.type.startsWith('image/')) {
        toast.error('Please select a valid image file for the icon');
        return;
      }
      if (type === 'file' && !file.name.endsWith('.zip')) {
        toast.error('Please select a valid ZIP file for the extension');
        return;
      }
      setFormData(prev => ({
        ...prev,
        [type]: file
      }));
    }
  };

  const removeFile = (type) => {
    setFormData(prev => ({
      ...prev,
      [type]: null
    }));
  };

  // Convert file to base64 data URL
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare extension data
      const extensionData = {
        name: formData.name,
        description: formData.description,
        category: formData.category,
        version: formData.version,
        isWhitelabeled: formData.isWhitelabeled,
        isUpgradeable: formData.isUpgradeable,
        repoMention: formData.repoMention,
        chromeWebStoreLink: formData.chromeWebStoreLink
      };

      // Convert icon to base64 if provided
      if (formData.icon) {
        try {
          const iconBase64 = await fileToBase64(formData.icon);
          extensionData.icon = iconBase64;
        } catch (error) {
          console.error('Error converting icon to base64:', error);
          toast.error('Error processing icon file');
          return;
        }
      }

      // Convert file to base64 if provided
      if (formData.file) {
        try {
          const fileBase64 = await fileToBase64(formData.file);
          extensionData.fileUrl = fileBase64;
          extensionData.fileName = formData.file.name;
        } catch (error) {
          console.error('Error converting file to base64:', error);
          toast.error('Error processing extension file');
          return;
        }
      }

      // Update extension via API
      const response = await extensionsAPI.update(id, extensionData);
      
      if (response.success) {
        toast.success('Extension updated successfully!');
        navigate(`/extension/${id}`);
      } else {
        // Handle validation errors
        if (response.errors && Array.isArray(response.errors)) {
          response.errors.forEach(error => toast.error(error));
        } else {
          toast.error(response.message || 'Failed to update extension');
        }
      }
    } catch (error) {
      console.error('Failed to update extension:', error);
      
      // Handle API errors with more detail
      if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to update extension. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingExtension) {
    return (
      <div className="create-extension-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading extension data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="create-extension-page">
      <div className="headerSection">
        <div className="page-header">
          <div className="page-title">
            <h2>Update Extension</h2>
            <p>Modify your browser extension settings</p>
          </div>
          <button className="back-btn" onClick={() => navigate(`/extension/${id}`)}>
            <FiArrowLeft />
            Back to Extension Details
          </button>
        </div>
      </div>

      <div className="dataArea-content">
        <div className="create-form-container">
          <form onSubmit={handleSubmit} className="create-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Extension Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter extension name"
                  required
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="form-control"
                >
                  <option value="">Select category</option>
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe your extension..."
                rows="4"
                className="form-control"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="version">Version</label>
                <input
                  type="text"
                  id="version"
                  name="version"
                  value={formData.version}
                  onChange={handleChange}
                  placeholder="1.0.0"
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label htmlFor="repoMention">Repository Mention</label>
                <input
                  type="text"
                  id="repoMention"
                  name="repoMention"
                  value={formData.repoMention}
                  onChange={handleChange}
                  placeholder="e.g., Feather Connector Whitelabel (V2.0.0)"
                  className="form-control"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="chromeWebStoreLink">Chrome Web Store Link</label>
                <input
                  type="url"
                  id="chromeWebStoreLink"
                  name="chromeWebStoreLink"
                  value={formData.chromeWebStoreLink}
                  onChange={handleChange}
                  placeholder="https://chrome.google.com/webstore/detail/..."
                  className="form-control"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isWhitelabeled"
                    checked={formData.isWhitelabeled}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span className="checkmark"></span>
                  Whitelabeled
                </label>
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="isUpgradeable"
                    checked={formData.isUpgradeable}
                    onChange={handleChange}
                    className="checkbox-input"
                  />
                  <span className="checkmark"></span>
                  Upgradeable
                </label>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="icon">Extension Icon (60x60)</label>
                <div className="file-upload">
                  {formData.icon ? (
                    <div className="file-preview">
                      <img src={URL.createObjectURL(formData.icon)} alt="Icon preview" />
                      <button
                        type="button"
                        onClick={() => removeFile('icon')}
                        className="remove-file"
                      >
                        <FiX />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-area">
                      <FiUpload />
                      <span>Click to upload new icon (60x60 PNG, JPG)</span>
                      <input
                        type="file"
                        id="icon"
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'icon')}
                        className="file-input"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="file">Extension File (Optional)</label>
                <div className="file-upload">
                  {formData.file ? (
                    <div className="file-preview">
                      <div className="file-info">
                        <span className="file-name">{formData.file.name}</span>
                        <span className="file-size">
                          {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile('file')}
                        className="remove-file"
                      >
                        <FiX />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-area">
                      <FiUpload />
                      <span>Click to upload new extension (ZIP) - Optional</span>
                      <input
                        type="file"
                        id="file"
                        accept=".zip"
                        onChange={(e) => handleFileChange(e, 'file')}
                        className="file-input"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={() => navigate(`/extension/${id}`)}
                className="btn btn-hollow"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !formData.name || !formData.category}
                className="btn btn-blue"
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    Updating...
                  </>
                ) : (
                  <>
                    <FiSave />
                    Update Extension
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateExtension; 